import { Injectable } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { get, del } from 'aws-amplify/api';

export type SortDirection = 'asc' | 'desc';

export type AccountSortField =
  | 'name'
  | 'email'
  | 'createdOn'
  | 'lastLogin'
  | 'acceptedTerms'
  | 'charity';

export type AccountSubscriptionType = string;

export type IHelp = Record<string, unknown>;

export type Provinces = string;

export interface IAccount {
  _id: string;
  userName: string;
  email: string;
  name: string;
  timezone?: string;
  usertype: {
    person: Boolean;
    releaser: Boolean;
  };
  subscription?: {
    tier: AccountSubscriptionType;
    storageLimit: number;
    expiration: Date;
    voucher?: string;
    customer?: string;
    charity?: string;
    stripe?: {
      customerId: string;
      subscriptionId: string;
    };
  };
  permissions?: {
    pushNotifications: boolean;
    upload: boolean;
    releaser: boolean;
    pdfExportUnlimited: boolean;
    email: boolean;
  };
  helpState?: IHelp;
  social?: {
    userId: string;
    providerName: string;
  };
  preferredLang: string;
  location?: Provinces;
  lastPDFExport?: Date;
  lastLogin?: Date;
  lastTOSNotificationDate: Date;
  _createdAt?: Date;
  _updatedAt?: Date;
  totalStorage: number;
  attachmentCount: number;
}

export type AccountListQuery = {
  sortField: AccountSortField;
  sortDirection: SortDirection;
  userType?: string;
  page: number;
  pageSize: number;
};

export type AccountListResponse = {
  items: IAccount[];
  totalCount: number;
  recordsFound: number;
  page: number;
  pageSize: number;
};

@Injectable({ providedIn: 'root' })
export class AccountsService {
  listAccounts(query: AccountListQuery): Observable<AccountListResponse> {
    const first = (query.page - 1) * query.pageSize;
    const sortOrder = query.sortDirection === 'asc' ? 1 : -1;

    return from(
      get({
        apiName: 'API',
        path: 'account',
        options: {
          queryParams: {
            rows: String(query.pageSize),
            first: String(first),
            sortField: query.sortField,
            sortOrder: String(sortOrder),
            filters: JSON.stringify({
              ...(query.userType === 'releaser' ? { releaser: { value: true } } : {}),
              ...(query.userType === 'user' ? { user: { value: true } } : {})
            })
          }
        }
      }).response.then((res) => res.body.json())
    ).pipe(
      map((res: any) => {
        const totalCount = toNumber(
          res.totalRecords ?? res.totalAccountRecords ?? res.totalCount ?? res.total ?? res.count
        );
        const recordsFound = toNumber(
          res.resultTotal ?? res.recordsFound ?? totalCount ?? res.accounts?.length
        );

        return {
          items: res.accounts ?? [],
          totalCount,
          recordsFound,
          page: query.page,
          pageSize: query.pageSize
        };
      })
    );
  }

  deleteAccount(accountId: string): Observable<any> {
    return from(
      del({
        apiName: 'API',
        path: `accounts`,
        options: {
          queryParams: {
            accountId
          }
        }
      }).response.then((res) => res.body.json())
    );
  }
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}
